import { useTranslations } from '@/contexts/translation/useTranslations';
import useGetAuthentication from '@/domains/authentication/useGetAuthentication.ts';
import LoginForm from '@/pages/authentication/Login/LoginForm/LoginForm.tsx';
import useAuthNavigator from '@/pages/authentication/navigation/useAuthNavigator.ts';
import ThemeButton from '@/theme/Components/ThemeButton/ThemeButton.tsx';
import ThemeTypography from '@/theme/Components/ThemeTypography/ThemeTypography';
import { Button, Divider, Flex, Typography } from 'antd';

interface LoginProps {
  handleForgotPassword: () => void;
}

const Login = ({ handleForgotPassword }: LoginProps) => {
  const { data } = useGetAuthentication();
  const { t } = useTranslations({ ns: 'auth' });
  const { userType, handleUserType } = useAuthNavigator();
  const isPartner = userType === 'partner';
  const isInvestor = userType === 'investor';
  const SSOLinkPartners = data?.pageData?.attributes?.SSOLinkPartners;
  const SSOLinkInvestors = data?.pageData?.attributes?.SSOLinkInvestors;
  const SSOLinkInvestorContacts =
    data?.pageData?.attributes?.SSOLinkInvestorContacts;

  return (
    <>
      <ThemeTypography style={{ textAlign: 'center' }} variant="h3">
        {t(`login.${userType}.welcome`)}
      </ThemeTypography>
      <ThemeTypography style={{ textAlign: 'center', marginBottom: 24 }}>
        {t(`login.${userType}.subtitle`)}
      </ThemeTypography>
      <LoginForm handleForgotPassword={handleForgotPassword} />
      <Flex
        gap={8}
        justify={'center'}
        style={{ paddingTop: 16, paddingInline: 16 }}
      >
        {isPartner && SSOLinkPartners && (
          <ThemeButton
            block
            onClick={() => window.open(SSOLinkPartners)}
            variant="outlined"
          >
            {t('login.common.actions.sso_partners_label')}
          </ThemeButton>
        )}
        {isInvestor && (
          <>
            {SSOLinkInvestors && (
              <ThemeButton
                block
                onClick={() => window.open(SSOLinkInvestors)}
                variant="outlined"
              >
                {t('login.common.actions.sso_investors_label')}
              </ThemeButton>
            )}
            {SSOLinkInvestorContacts && (
              <ThemeButton
                block
                onClick={() => window.open(SSOLinkInvestorContacts)}
                variant="outlined"
              >
                {t('login.common.actions.sso_investor_contacts_label')}
              </ThemeButton>
            )}
          </>
        )}
      </Flex>

      <Divider />
      {isPartner &&
        data?.pageData?.attributes?.partnerSecondaryButtonLabel &&
        data?.pageData?.attributes?.partnerSecondaryButtonUrl && (
          <div style={{ width: '100%' }}>
            <ThemeButton
              block
              onClick={() =>
                window.open(data.pageData.attributes.partnerSecondaryButtonUrl)
              }
              variant="outlined"
            >
              {data?.pageData?.attributes?.partnerSecondaryButtonLabel}
            </ThemeButton>
          </div>
        )}
      {userType === 'investor' &&
        data?.pageData?.attributes?.investorSecondaryButtonLabel &&
        data?.pageData?.attributes?.investorSecondaryButtonUrl && (
          <div style={{ width: '100%' }}>
            <ThemeButton
              block
              onClick={() =>
                window.open(data.pageData.attributes.investorSecondaryButtonUrl)
              }
              style={{ flex: 1 }}
              variant="outlined"
            >
              {data?.pageData?.attributes?.investorSecondaryButtonLabel}
            </ThemeButton>
          </div>
        )}
      {isPartner
        ? data?.pageData?.attributes?.rules?.investor?.showSwitchBtn && (
            <Button type="link">
              <Typography.Link onClick={handleUserType('investor')}>
                {t('login.common.actions.redirect_login_investor')}
              </Typography.Link>
            </Button>
          )
        : data?.pageData?.attributes?.rules?.investor?.showSwitchBtn && (
            <Button variant="link">
              <Typography.Link onClick={handleUserType('partner')}>
                {t('login.common.actions.redirect_login_partner')}
              </Typography.Link>
            </Button>
          )}
    </>
  );
};

export default Login;
